import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader, CLoader } from '../../components/common';
import { FiVideo, FiMessageCircle, FiCalendar, FiClock } from 'react-icons/fi';
import { fetchUserBookings, acceptBooking, cancelBooking, Booking } from '../../api';

const AllBookings: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);

  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selectedTab, setSelectedTab] = useState<'Bookings' | 'Sessions'>('Bookings');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const data = await fetchUserBookings();
      setBookings(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const isUpcoming = (booking: Booking) => {
    const bookingEndTime = new Date(booking.start_time).getTime() + 30 * 60 * 1000;
    return bookingEndTime > Date.now();
  };

  const upcomingBookings = bookings.filter(isUpcoming);
  const pastSessions = bookings.filter((b) => !isUpcoming(b));

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    const endDate = new Date(date.getTime() + 30 * 60 * 1000);
    return `${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })} - ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  };

  const canJoin = (booking: Booking) => {
    const now = Date.now();
    const startTime = new Date(booking.start_time).getTime();
    const endTime = startTime + 30 * 60 * 1000;
    // Can join 5 minutes before start time until end time
    return now >= startTime - 5 * 60 * 1000 && now <= endTime;
  };

  const handleJoinSession = (booking: Booking) => {
    if (booking.booking_type === 'V') {
      navigate(`/call/${booking.id}?expert=${encodeURIComponent(booking.expert)}`);
    } else {
      navigate(`/chat/${booking.id}?expert=${encodeURIComponent(booking.expert)}`);
    }
  };

  const handleAcceptBooking = async (bookingId: number) => {
    setLoading(true);
    try {
      await acceptBooking(bookingId);
      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, is_booked: true } : b))
      );
    } catch (error) {
      console.error('Error accepting booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = (bookingId: number) => {
    setSelectedBookingId(bookingId);
    setCancelModalOpen(true);
  };

  const confirmCancelBooking = async () => {
    if (!selectedBookingId) return;
    setLoading(true);
    try {
      await cancelBooking(selectedBookingId);
      setBookings((prev) => prev.filter((b) => b.id !== selectedBookingId));
      setCancelModalOpen(false);
      setSelectedBookingId(null);
    } catch (error) {
      console.error('Error canceling booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReschedule = (booking: Booking) => {
    navigate(`/expert/${booking.expert_id}/book?type=${booking.booking_type === 'V' ? 'call' : 'chat'}`);
  };

  const handleViewSession = (booking: Booking) => {
    // For past chat sessions, could show history
    // For now, navigate to rate if not rated
    navigate(`/rate/${booking.id}?expert=${encodeURIComponent(booking.expert)}`);
  };

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="lg:max-w-5xl lg:mx-auto">
      {loading && <CLoader />}

      <CHeader title="Your Bookings" />

      {/* Tab Selector */}
      <div
        className="flex rounded-full p-1 mb-6"
        style={{ backgroundColor: colors.inputBackground }}
      >
        {(['Bookings', 'Sessions'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setSelectedTab(tab)}
            className="flex-1 py-2 rounded-full transition-colors"
            style={{
              backgroundColor: selectedTab === tab ? colors.primary : 'transparent',
            }}
          >
            <CText
              type="M14"
              color={selectedTab === tab ? colors.white : colors.grayScale500}
            >
              {tab}
            </CText>
          </button>
        ))}
      </div>

      {/* Bookings Tab - Upcoming */}
      {selectedTab === 'Bookings' && (
        <div>
          {upcomingBookings.length === 0 ? (
            <div className="text-center py-12">
              <CText type="r14" color={colors.grayScale500}>
                No upcoming bookings
              </CText>
              <button
                onClick={() => navigate('/experts')}
                className="mt-4 px-6 py-2 rounded-full"
                style={{ backgroundColor: colors.primary }}
              >
                <CText type="M14" color={colors.white}>
                  Book an Expert
                </CText>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingBookings.map((booking) => (
              <div
                key={booking.id}
                className="rounded-xl p-4"
                style={{ backgroundColor: colors.inputBackground }}
              >
                {/* Header Row */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="p-2 rounded-full"
                      style={{ backgroundColor: colors.primary + '20' }}
                    >
                      {React.createElement(
                        booking.booking_type === 'V' ? FiVideo : FiMessageCircle as any,
                        { size: 16, color: colors.primary }
                      )}
                    </div>
                    <CText type="B16" color={colors.textColor}>
                      {booking.expert}
                    </CText>
                  </div>
                  {!booking.is_booked && (
                    <span
                      className="px-2 py-1 rounded text-xs"
                      style={{ backgroundColor: colors.alertColor + '20', color: colors.alertColor }}
                    >
                      Pending
                    </span>
                  )}
                </div>

                {/* Date & Time */}
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    {React.createElement(FiCalendar as any, { size: 14, color: colors.grayScale500 })}
                    <CText type="r14" color={colors.textColor}>
                      {formatDate(booking.start_time)}
                    </CText>
                  </div>
                  <div className="flex items-center gap-2">
                    {React.createElement(FiClock as any, { size: 14, color: colors.grayScale500 })}
                    <CText type="r14" color={colors.textColor}>
                      {formatTime(booking.start_time)}
                    </CText>
                  </div>
                </div>

                {/* Join Link */}
                {booking.is_booked && (
                  <button
                    onClick={() => handleJoinSession(booking)}
                    disabled={!canJoin(booking)}
                    className="mb-3"
                    style={{
                      opacity: canJoin(booking) ? 1 : 0.5,
                      cursor: canJoin(booking) ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <CText type="M14" color={colors.primary}>
                      {canJoin(booking)
                        ? booking.booking_type === 'V'
                          ? 'Join Call →'
                          : 'Start Chat →'
                        : 'Waiting for session time...'}
                    </CText>
                  </button>
                )}

                {/* Action Buttons */}
                {!booking.is_booked && (
                  <div className="flex gap-3 mt-2">
                    {(user as any)?.is_expert ? (
                      <button
                        onClick={() => handleAcceptBooking(booking.id)}
                        className="flex-1 py-2 rounded-lg border"
                        style={{ borderColor: colors.primary }}
                      >
                        <CText type="M14" color={colors.primary}>
                          Accept
                        </CText>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReschedule(booking)}
                        className="flex-1 py-2 rounded-lg border"
                        style={{ borderColor: colors.borderColor }}
                      >
                        <CText type="M14" color={colors.primary}>
                          Re-Schedule
                        </CText>
                      </button>
                    )}
                    <button
                      onClick={() => handleCancelBooking(booking.id)}
                      className="flex-1 py-2 rounded-lg border"
                      style={{ borderColor: colors.borderColor }}
                    >
                      <CText type="M14" color={colors.alertColor}>
                        Cancel
                      </CText>
                    </button>
                  </div>
                )}
              </div>
            ))}
            </div>
          )}
        </div>
      )}

      {/* Sessions Tab - Past */}
      {selectedTab === 'Sessions' && (
        <div>
          {pastSessions.length === 0 ? (
            <div className="text-center py-12">
              <CText type="r14" color={colors.grayScale500}>
                No past sessions
              </CText>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pastSessions.map((booking) => (
                <button
                  key={booking.id}
                  onClick={() => handleViewSession(booking)}
                  className="w-full rounded-xl p-4 text-left"
                  style={{ backgroundColor: colors.inputBackground }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="p-2 rounded-full"
                        style={{ backgroundColor: colors.grayScale500 + '20' }}
                      >
                        {React.createElement(
                          booking.booking_type === 'V' ? FiVideo : FiMessageCircle as any,
                          { size: 16, color: colors.grayScale500 }
                        )}
                      </div>
                      <div>
                        <CText type="M14" color={colors.textColor}>
                          {booking.expert}
                        </CText>
                        <CText type="r12" color={colors.grayScale500}>
                          {formatDate(booking.start_time)}
                        </CText>
                      </div>
                    </div>
                    <CText type="r12" color={colors.primary}>
                      View →
                    </CText>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {cancelModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="rounded-2xl p-6 w-full max-w-sm"
            style={{ backgroundColor: colors.backgroundColor }}
          >
            <CText type="B18" color={colors.textColor} style={{ marginBottom: 8 }}>
              Cancel Booking?
            </CText>
            <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 24 }}>
              Are you sure you want to cancel this booking? This action cannot be undone.
            </CText>
            <div className="flex gap-3">
              <button
                onClick={() => setCancelModalOpen(false)}
                className="flex-1 py-3 rounded-xl border"
                style={{ borderColor: colors.borderColor }}
              >
                <CText type="M14" color={colors.textColor}>
                  Keep Booking
                </CText>
              </button>
              <button
                onClick={confirmCancelBooking}
                className="flex-1 py-3 rounded-xl"
                style={{ backgroundColor: colors.alertColor }}
              >
                <CText type="M14" color={colors.white}>
                  Cancel
                </CText>
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AllBookings;
